import { Mail, MapPin, Building2, FileText, Scale, Globe } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSeo } from "@/hooks/useSeo";
import { cn } from "@/lib/utils";

interface ImpressumSeiteProps {
  onNavigate: (path: string) => void;
}

export function ImpressumSeite({ onNavigate }: ImpressumSeiteProps) {
  useSeo({
    title: "Impressum - zelt.kaufen",
    description: "Rechtliche Informationen und Impressum von zelt.kaufen gemäß § 5 TMG.",
    type: "website",
  });

  return (
    <div className="min-h-screen py-12">
      <Container className="max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Impressum</h1>
          <p className="text-lg text-muted-foreground">
            Angaben gemäß § 5 TMG
          </p>
        </div>

        <div className="space-y-8">
          {/* Anbieter */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Angaben gemäß § 5 TMG
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-semibold text-lg">Pykez UG (haftungsbeschränkt)</p>
                <p className="text-muted-foreground">Danziger Str. 4a</p>
                <p className="text-muted-foreground">36251 Bad Hersfeld</p>
                <p className="text-muted-foreground">Deutschland</p>
              </div>

              <div className="pt-4 border-t space-y-2">
                <p><span className="font-medium">USt-IdNr.:</span> DE337013420</p>
                <p><span className="font-medium">Handelsregister:</span> HRB 3034</p>
                <p><span className="font-medium">Registergericht:</span> Amtsgericht Bad Hersfeld</p>
              </div>
            </CardContent>
          </Card>

          {/* Kontakt */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Kontakt
              </CardTitle>
            </CardHeader>
            <CardContent>
              <a
                href="mailto:mail@pykez.com"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <Mail className="h-4 w-4" />
                mail@pykez.com
              </a>
            </CardContent>
          </Card>

          {/* Haftungsausschluss */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-primary" />
                Haftungsausschluss
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Haftung für Inhalte</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit,
                  Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.
                  Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten
                  nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als
                  Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde
                  Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige
                  Tätigkeit hinweisen.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Haftung für Links</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen
                  Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.
                  Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der
                  Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf
                  mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der
                  Verlinkung nicht erkennbar.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Urheberrecht */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Urheberrecht
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen
                dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art
                der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen
                Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite
                sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.
              </p>
            </CardContent>
          </Card>

          {/* Bildquellen */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Bildquellen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                Die auf dieser Website verwendeten Bilder stammen aus verschiedenen lizenzierten Quellen
                und unterliegen dem jeweiligen Urheberrecht der Rechteinhaber.
              </p>
            </CardContent>
          </Card>

          {/* Affiliate-Hinweis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Affiliate-Hinweis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                Diese Website enthält Affiliate-Links zu Partnerseiten. Bei einem Kauf über diese Links
                erhalten wir eine kleine Provision, ohne dass für Sie zusätzliche Kosten entstehen.
                Dies hilft uns, die Website zu finanzieren und weiterhin unabhängige Vergleiche und
                Bewertungen anzubieten.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Back Link */}
        <div className="mt-12 text-center">
          <button
            onClick={() => onNavigate("/")}
            className="text-primary hover:underline"
          >
            ← Zurück zur Startseite
          </button>
        </div>
      </Container>
    </div>
  );
}
